import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Bot, Workflow, LayoutDashboard, Sparkles } from "lucide-react";

const PROMPTS = ["Summarise this week's sales pipeline", "Draft a follow-up to new leads", "Automate invoice reminders every Monday"];

const useTyping = () => {
  const [i, setI] = useState(0);
  const [txt, setTxt] = useState("");
  useEffect(() => {
    const full = PROMPTS[i];
    if (txt.length < full.length) {
      const t = setTimeout(() => setTxt(full.slice(0, txt.length + 1)), 45);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => { setTxt(""); setI((i + 1) % PROMPTS.length); }, 2200);
    return () => clearTimeout(t);
  }, [txt, i]);
  return txt;
};

export const VoiceOrb = ({ size = 180 }) => (
  <div className="relative flex items-center justify-center" style={{ width: size, height: size }} data-testid="ai-voice-orb">
    {[0, 1, 2].map((k) => <span key={k} className="absolute inset-0 rounded-full border border-[#00f0ff]/50 pulse-ring" style={{ animationDelay: `${k * 0.8}s` }} />)}
    <div className="absolute inset-[18%] rounded-full bg-[radial-gradient(circle_at_35%_30%,#7dd3fc,#0066ff_45%,#020617_80%)] shadow-[0_0_60px_rgba(0,240,255,0.5)]" />
    <div className="relative flex items-end gap-1 h-10">
      {[0.2, 0.5, 0.8, 0.35, 0.65, 0.1, 0.45].map((d, k) => <span key={k} className="eq-bar w-1.5 h-full rounded-full bg-white/90" style={{ animationDelay: `${d}s` }} />)}
    </div>
  </div>
);

const Spark = () => (
  <svg viewBox="0 0 200 60" className="w-full h-16" aria-hidden="true">
    <defs><linearGradient id="sp" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor="#00f0ff" stopOpacity=".5" /><stop offset="1" stopColor="#00f0ff" stopOpacity="0" /></linearGradient></defs>
    <path d="M0 50 L25 42 L50 46 L75 30 L100 34 L125 20 L150 26 L175 12 L200 16 L200 60 L0 60 Z" fill="url(#sp)" />
    <motion.path d="M0 50 L25 42 L50 46 L75 30 L100 34 L125 20 L150 26 L175 12 L200 16" fill="none" stroke="#00f0ff" strokeWidth="2"
      initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 2 }} />
  </svg>
);

export const AIMockup = () => {
  const txt = useTyping();
  return (
    <div className="relative rounded-3xl border border-[#00f0ff]/25 bg-[#050a16]/80 backdrop-blur-xl p-4 sm:p-6 overflow-hidden shadow-[0_0_80px_-20px_rgba(0,240,255,0.35)]" data-testid="ai-os-mockup">
      <div className="absolute inset-0 grid-lines opacity-40" />
      <motion.div className="absolute inset-x-0 h-24 bg-gradient-to-b from-transparent via-[#00f0ff]/10 to-transparent pointer-events-none" animate={{ y: ["-30%", "520%"] }} transition={{ duration: 5, repeat: Infinity, ease: "linear" }} />
      <div className="relative grid md:grid-cols-[auto_1fr] gap-6 items-center">
        <div className="flex justify-center"><VoiceOrb /></div>
        <div className="space-y-3">
          <p className="font-mono text-[11px] text-[#7dd3fc] tracking-wider flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> BOSS AI · COMMAND</p>
          <div className="field !bg-black/40 font-mono text-sm min-h-[52px] text-[#e0f2fe]" data-testid="ai-command-line">
            {txt}<span className="inline-block w-2 h-4 bg-[#00f0ff] ml-0.5 align-middle animate-pulse" />
          </div>
        </div>
      </div>
      <div className="relative grid sm:grid-cols-3 gap-3 mt-6">
        <div className="rounded-2xl border border-white/10 bg-black/30 p-4">
          <p className="flex items-center gap-2 text-xs text-slate-300 mb-3"><Bot className="w-4 h-4 text-[#d4af37]" /> Agents</p>
          {["Research", "Sales", "Operations"].map((a, k) => (
            <p key={a} className="flex items-center justify-between text-[12px] text-white py-1.5 border-b border-white/5 last:border-0">
              {a}<span className={`w-1.5 h-1.5 rounded-full ${k === 2 ? "bg-white/30" : "bg-[#00f0ff] animate-pulse"}`} />
            </p>
          ))}
        </div>
        <div className="rounded-2xl border border-white/10 bg-black/30 p-4">
          <p className="flex items-center gap-2 text-xs text-slate-300 mb-3"><Workflow className="w-4 h-4 text-[#d4af37]" /> Workflow</p>
          <div className="flex items-center justify-between">
            {["Trigger", "AI", "Action"].map((s, k) => (
              <div key={s} className="flex items-center">
                <span className="rounded-lg border border-[#00f0ff]/40 px-2 py-1.5 text-[11px] text-[#e0f2fe]">{s}</span>
                {k < 2 ? <svg width="18" height="8" className="mx-0.5"><line x1="0" y1="4" x2="18" y2="4" stroke="#00f0ff" className="dash-flow" /></svg> : null}
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-black/30 p-4">
          <p className="flex items-center gap-2 text-xs text-slate-300 mb-1"><LayoutDashboard className="w-4 h-4 text-[#d4af37]" /> Dashboard</p>
          <Spark />
        </div>
      </div>
      <p className="relative mt-4 font-mono text-[11px] text-slate-400 tracking-wider">CONCEPT INTERFACE · ILLUSTRATIVE ONLY</p>
    </div>
  );
};
